import {typeAffirmer} from '@player/src/mixins';
import Hooks from '@player/src/hooks';
import {SubtitleController} from '@player/src/main/subtitle';


/**
* @class @function VideoController:
* 

* @property {Boolean} muted:mapped to @property {HTMLVideoElement} _videoTag.muted 
* @property {Number range(0, 1)} volume:mapped to @property {HTMLVideoElement} _videoTag.volume   
* @property {Instance of Hook} hub:<required><useraccessible>
* @property {Instance of SubtitleController} subtitle:<required><useraccessible>
* @property {Object} _settings:<userinput> @param {Object} settings
* @property {HTMLVideoElement} _videoTag:<userinput>
* @property {Number} _seekduration:derived from @property {Object} _settings.seekduration
* @property {Boolean} _loadplaying: set true while play request is active.
 
 * 
 * ---------------------------------
 * @constructor: @param {Object} { @property {Object} settings }
 * - create hub for event management.
 * - assign settings and default _seekduration.
 * 
 *  * 
 * ---------------------------------
 * @user_accessible @getters :
 * 
 * @getter @property { Boolean } paused: 
 * - @returns true if video content is paused, else false. 
 * 
 * ---------------------------------
 * @user_accessible @methods :
 * 
 * @method setVideoTag: @param {HTMLElement} videoTag
 * - to be called once videoTag element is available to be attached for reference.
 * - @calls @method _attachVideoListeners if video Listeners are required.
 * 
 * @method play @async:
 * - requests video element to play the content.
 * - @returns a promise to be resolved once the play starts.
 *  
 * @method pause:
 * - request video element to pause the playback.
 * 
 * @getter paused:
 * - @returns play/pause status of video element.
 * 
 * @method setVolume: @param {Function} reducer
 * - request to update the volume according to @return value of reducer.
 * @calls @function reducer @expects @return {Number} 
 * 
 * 
 * @method toggleMute: @param {Boolean} value <optional>
 * - mute/unmute video according to @param value passed or toggle it.
 * - maps @property {Boolean} this.muted w.r.t the current mute value.
 * 
 * @method seekTrack @async: @param {Number} time (in seconds)
 * - set @property {Number} this._videoTag.currentTime to the @property {Number} time passed.
 * - @returns a promise which is resolved when the seek has completed.
 * 
 * @method forwards:
 * - add @property {Number} this_videoTag.currentTime with @property {Number} this._seekduration
 * 
 * @method backwards:
 * - reduce @property {Number} this_videoTag.currentTime with @property {Number} this._seekduration
 * -------------------------------------------------
 * 
 * @internal @methods :
 * 
 * @method _attachVideoListeners
 * - attach the hub to the events emitted by the video Element.
 */
export default class VideoController{
    constructor(settings){
        this.hub=new Hooks();
        this.subtitle=null;
        this._settings=settings;
        this._seekduration=this._settings.seekduration??10;
        this.volume=this._settings.initialVolume??1;
        this._videoTag=null;
        this._loadplaying=null;
        this.muted=false;
    }

    get paused(){
        return  this._videoTag?.paused ?? false;
    }

    get duration(){
        return this._videoTag?.duration ?? 0 ;
    }

    setVideoTag(videoTag){
        if(videoTag instanceof HTMLVideoElement){
            this._videoTag=videoTag;
            this.subtitle=new SubtitleController({videoTag,hub:this.hub});
            if(this._settings.viewInjected)this._attachVideoListeners();
            this.setVolume(()=>this.volume);
        }
    }

    play(){
        if(!this._videoTag)return Promise.resolve();
        this._loadplaying=true;
        return this._videoTag.play().then(()=>{
            this._loadplaying=false;
        });
    }
    pause(){
        if(!this._videoTag)return;
        if(this._videoTag.paused || this._loadplaying)return;
        this._videoTag.pause();
    }
      
    setVolume(reducer){
        if(!this._videoTag)return;
        this._videoTag.muted=this.muted=false;
        if(!typeAffirmer('function')(reducer))return;
        let value=reducer(this._videoTag.volume);
        if(!typeAffirmer('number')(value))return;
        if(value<0)value=0;
        if(value>1)value=1;
        this._videoTag.volume=this.volume=value;
    }
    toggleMute(value){
        if(!this._videoTag)return;
        if(!value){
            this._videoTag.muted=!this._videoTag.muted;
        }
        else if(typeAffirmer('boolean')(value)){
            this._videoTag.muted=value;
        }
        this.muted=this._videoTag.muted;
    }

    seekTrack(time){
        if(!this._videoTag)return Promise.resolve();
        if(!typeAffirmer('number')(time))return;
        let duration=this._videoTag.duration;
        if(time<0 || time>duration)return;
        this._videoTag.currentTime=time;
        return new Promise(resolve=>{
            this._videoTag.addEventListener('seeked',()=>{
                resolve();
            },{once:true})
        })
    }

    forwards(){
        if(!this._videoTag)return;
        let duration=this._videoTag.duration;
        if(this._seekduration>=( duration - this._videoTag.currentTime)){
            this._videoTag.currentTime=duration;
        }
        else {
            this._videoTag.currentTime+=this._seekduration;
        }
        this.hub.dispatch('controller_forwards')
    }
    backwards(){
        if(!this._videoTag)return;
        if(this._videoTag.currentTime<=this._seekduration){
            this._videoTag.currentTime=0;
        }
        else {
            this._videoTag.currentTime-=this._seekduration;
        }
        this.hub.dispatch('controller_backwards');
    }
    destroy(){
        this._videoTag=null;
        this.subtitle.destroy();
        this.hub.destroy();
    }
    _attachVideoListeners(){
        function _videoListeners(eventname,data){
            this.hub.dispatch(eventname,data);
        }
        this._videoTag.addEventListener("loadstart", _videoListeners.bind(this,'loadstart'));
        this._videoTag.addEventListener("loadedmetadata", _videoListeners.bind(this,'loadedmetadata'));
        this._videoTag.addEventListener("loadeddata", _videoListeners.bind(this,'loadeddata'));
        this._videoTag.addEventListener("timeupdate", _videoListeners.bind(this,'timeupdate'));
        this._videoTag.addEventListener("ended", _videoListeners.bind(this,'ended'));
        this._videoTag.addEventListener("seeking", _videoListeners.bind(this,'seeking'));
        this._videoTag.addEventListener("seeked", _videoListeners.bind(this,'seeked'));
        this._videoTag.addEventListener("play",_videoListeners.bind(this,'play'));
        this._videoTag.addEventListener("pause", _videoListeners.bind(this,'pause'));
        this._videoTag.addEventListener("progress", _videoListeners.bind(this,'progress'));
    }
}